import {faBell, faMinus, faPlus} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {useQuery} from '@apollo/react-hooks';
import {withTranslation} from 'react-i18next';
import {Alert, Button, Col, Container, Row} from 'react-bootstrap';
import {Card, CardBody, CardTitle} from 'reactstrap';
import styled from 'styled-components';
import Screen from '../../../common/components/layout/Screen';
import Metadata from '../../../common/components/Metadata';
import Table from '../../../common/components/Table/Table';
import {CancelButton, CenteredP, FooterRow, GradientCol, TitleButton} from '../../../common/components/StyledComp';
import {BADGE_COLORS} from '../../../common/constants';
import {PulseLoader} from 'react-spinners';
import DisplayAmount, {formatAmount} from '../../../common/components/DisplayAmount';
import {numberFormatThousands} from '../../../utils';
import Badge from '../../../common/components/Badge';
import {flowRight as compose} from 'lodash';
import {withRouter} from 'react-router';
import PaymentMethodManagement from '../../stripe/components/PaymentMethodManagement';
import {withLoadedUser} from '../../../login/containers/AuthBase';
import PopIn from '../../../common/components/PopIn';
import BuyProducts from '../../stripe/components/BuyProducts';

const Span = styled.span`
  font-family: 'Segoe UI Bold', sans-serif;
`;

const DetailRow = styled(Row)`
  align-items: center;
  margin-bottom: 5px;
  margin-top: 5px;
`;

const Value = styled.div`
  text-align: right;
  padding: 10px;
  background-color : #edeeee;
  border-radius: 4px;
`;

const QuantitySelected = styled.span`
  margin: 0 5px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GRAPHQL_QUERY_BILLING_TAX = gql`
  query billingTaxRate {
    billingTaxRate {
      id
      percentage
    }
  }
`;

export const GRAPHQL_QUERY_PRODUCTS = gql`
  query products($criteria: CriteriaInput!) {
    products(criteria: $criteria) {
      id
      rows {
        id
        description
        price
        quantity
      }
    }
  }
`;

const BuyNotification = ({t, history, currentUser}) => {

  const [items, setItems] = useState({});
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const {loading: loadingTax, error: errorTax, data: dataTax} = useQuery(GRAPHQL_QUERY_BILLING_TAX);

  if (errorTax) {
    console.error(errorTax);
  }

  const [totalPriceWithoutTaxes, totalNotifications, cart] = Object.values(items).reduce(
    ([totalPriceWithoutTaxes, totalNotifications, cart], {product, quantity}) => {
      if (quantity > 0) {
        cart.items.push({productId: product.id, quantity});
      }
      return [
        totalPriceWithoutTaxes + (product.price * quantity),
        totalNotifications + (product.quantity * quantity),
        cart,
      ];
    }, [0, 0, {items: []}]);
  const vat = dataTax && dataTax.billingTaxRate && dataTax.billingTaxRate.percentage ? dataTax.billingTaxRate.percentage : 0;
  const totalPriceTaxes = totalPriceWithoutTaxes * (vat / 100);
  const totalPriceWithTaxes = totalPriceWithoutTaxes + totalPriceTaxes;
  const totalPriceWithoutTaxesPerNotifications = totalPriceWithoutTaxes > 0 && totalNotifications > 0 ? (totalPriceWithoutTaxes / totalNotifications) : 0;

  const removeProduct = product => {
    setItems({
      ...items,
      [product.id]: {
        product,
        quantity: items[product.id].quantity > 0 ? items[product.id].quantity - 1 : 0,
      },
    });
  };

  const addProduct = product => {
    setItems({
      ...items,
      [product.id]: {
        product,
        quantity: items[product.id] ? (items[product.id].quantity + 1) : 1,
      },
    });
  };

  const columns = () => [
    {
      Header: t('buyNotifications.description'),
      accessor: 'description',
    },
    {
      Header: t('buyNotifications.quantityNotifications'),
      accessor: 'quantity',
      Cell: ({cell}) => numberFormatThousands(cell.value),
      sortable: true,
    },
    {
      Header: t('buyNotifications.pricePerNotification'),
      accessor: row => row,
      Cell: ({cell}) => <DisplayAmount
        amount={cell.value.price > 0 && cell.value.quantity > 0 ? (cell.value.price / cell.value.quantity) : 0}
        amountOptions={{minimumFractionDigits: 3}}
      />,
    },
    {
      Header: t('buyNotifications.price'),
      accessor: 'price',
      Cell: ({cell}) => <DisplayAmount amount={cell.value}/>,
      sortable: true,
    },
    {
      Header: t('buyNotifications.buyQuantity'),
      Cell: ({cell}) => {
        const product = cell.value;
        return (
          <Actions>
            {(items[product.id] && items[product.id].quantity > 0) && (
              <>
                <Button className={'btn-update'} onClick={() => removeProduct(product)}>
                  <FontAwesomeIcon icon={faMinus}  color={BADGE_COLORS.BLUE} size="sm"/>
                </Button>
                <QuantitySelected>
                  <Badge>{items[product.id].quantity}</Badge>
                </QuantitySelected>
              </>
            )}
            <Button className="btn-update" onClick={() => addProduct(product)}>
              <FontAwesomeIcon icon={faPlus} color={BADGE_COLORS.BLUE} size="sm"/>
            </Button>
          </Actions>
        );
      },
      accessor: row => row,
    },
  ];

  return (
    <Screen
      header={<Metadata meta={t('meta')} title={t('buyNotifications.title')}/>}
      leftTitle={
        <>
          <FontAwesomeIcon icon={faBell} color={'rgb(201,0,95)'}/>
          <Span className="route-name">{t('buyNotifications.title')}</Span>
        </>
      }>
      <Container fluid>
        <Table
          query={GRAPHQL_QUERY_PRODUCTS}
          keyResult="products"
          columns={columns()}
          defaultSort={{field: 'quantity'}}
        />
        <Row>
          <Col>
            <Card>
              <CardBody>
                <PaymentMethodManagement userId={currentUser.id}/>
              </CardBody>
              {!currentUser.hasValidPaymentMethod && (
                <CardBody>
                  <Alert variant={'info'}>{t('buyNotifications.paymentMethodRequired')}</Alert>
                </CardBody>
              )}
            </Card>
          </Col>
          <Col>
            <Card>
              <CardBody>
                <CardTitle>
                  <h5>{`${t('buyNotifications.commandDetails')} : `}</h5>
                </CardTitle>
                <hr/>
                <DetailRow>
                  <Col>
                    {`${t('buyNotifications.priceWithoutTaxes')} : `}
                  </Col>
                  <Col xs={6} xl={4}>
                    <Value>{formatAmount(totalPriceWithoutTaxes)}</Value>
                  </Col>
                </DetailRow>
                <DetailRow>
                  <Col>
                    {`${t('buyNotifications.taxRate')} : `}
                  </Col>
                  <Col xs={6} xl={4}>
                    <Value>{loadingTax ? <PulseLoader/> : vat + ' %'}</Value>
                  </Col>
                </DetailRow>
                <DetailRow>
                  <Col>
                    {`${t('buyNotifications.priceTaxes')} : `}
                  </Col>
                  <Col xs={6} xl={4}>
                    <Value>{loadingTax ? <PulseLoader/> : formatAmount(totalPriceTaxes)}</Value>
                  </Col>
                </DetailRow>
                <hr/>
                <DetailRow>
                  <Col>
                    <strong>{`${t('buyNotifications.priceWithTaxes')} : `}</strong>
                  </Col>
                  <Col xs={6} xl={4}>
                    <Value><strong>{loadingTax ? <PulseLoader/> : formatAmount(totalPriceWithTaxes)}</strong></Value>
                  </Col>
                </DetailRow>
                <hr/>
                <DetailRow>
                  <Col>
                    {`${t('buyNotifications.totalNotifications')} : `}
                  </Col>
                  <Col xs={6} xl={4}>
                    <Value>{numberFormatThousands(totalNotifications)}</Value>
                  </Col>
                </DetailRow>
                <DetailRow>
                  <Col>
                    {`${t('buyNotifications.priceWithoutTaxesPerNotification')} : `}
                  </Col>
                  <Col xs={6} xl={4}>
                    <Value>{loadingTax ? <PulseLoader/> : formatAmount(totalPriceWithoutTaxesPerNotifications, {minimumFractionDigits: 3})}</Value>
                  </Col>
                </DetailRow>
              </CardBody>
              {totalPriceWithTaxes <= 0 && (
                <CardBody>
                  <Alert variant={'info'}>{t('buyNotifications.atLeastOnProductRequired')}</Alert>
                </CardBody>
              )}
            </Card>
          </Col>
        </Row>
        <FooterRow>
          <GradientCol sm={{span: 6, offset: 3}}>
            <div className="popin-buttons">
              <TitleButton type="submit" disabled={totalPriceWithTaxes <= 0 || !currentUser.hasValidPaymentMethod} onClick={() => {setConfirmModalVisible(true);}}>
                {t('buyNotifications.order')}
              </TitleButton>
              <CancelButton onClick={() => {history.push('/admin/campaign');}} style={{marginTop: 0, width: 'auto'}}>
                {t('buyNotifications.cancel')}
              </CancelButton>
            </div>
          </GradientCol>
        </FooterRow>

        <PopIn visible={confirmModalVisible} setModalVisible={value => !orderLoading && setConfirmModalVisible(value)} title={t('buyNotifications.confirmOrder')}>
          <Container>
            {!orderLoading && (
              <CenteredP>
                {t('buyNotifications.confirmOrderMessage', {totalPrice: formatAmount(totalPriceWithTaxes), totalNotifications: numberFormatThousands(totalNotifications)})}
              </CenteredP>
            )}
            <BuyProducts
              cart={cart}
              onStart={() => setOrderLoading(true)}
              onError={() => setOrderLoading(false)}
              onComplete={() => {history.push('/admin/campaign?newNotifications=true');}}
              onCancel={() => setConfirmModalVisible(false)}
            />
          </Container>
        </PopIn>
      </Container>
    </Screen>
  );
};

BuyNotification.propTypes = {
  t: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    hasValidPaymentMethod: PropTypes.bool.isRequired,
  }).isRequired,
};

export default compose(
  withTranslation('products'),
  withRouter,
  withLoadedUser,
)(BuyNotification);
